const selectors = {
  popoutWrapper: '[data-popout]',
  popoutList: '[data-popout-list]',
  popoutToggle: '[data-popout-toggle]',
  popoutInput: '[data-popout-input]',
  popoutOptions: '[data-popout-option]',
  popoutPrevent: 'data-popout-prevent',
  popoutQuantity: 'data-quantity-field',
  dataValue: 'data-value',
  ariaExpanded: 'aria-expanded',
  ariaCurrent: 'aria-current',
  productGridImage: '[data-product-image]',
  productGrid: '[data-product-grid-item]',
};

const classes = {
  listVisible: 'popout-list--visible',
  currentSuffix: '--current',
  popoutAlternative: 'popout-container--alt',
  visible: 'is-visible',
};

let sections = {};

class Popout {
  constructor(popout) {
    this.container = popout;
    this.popoutList = this.container.querySelector(selectors.popoutList);
    this.popoutToggle = this.container.querySelector(selectors.popoutToggle);
    this.popoutInput = this.container.querySelector(selectors.popoutInput);
    this.popoutOptions = this.container.querySelectorAll(selectors.popoutOptions);
    this.productGrid = this.popoutList.closest(selectors.productGrid);
    this.popoutPrevent = this.container.getAttribute(selectors.popoutPrevent) === 'true';
    this.popupToggleFocusoutEvent = (evt) => this.popupToggleFocusout(evt);
    this.popupListFocusoutEvent = (evt) => this.popupListFocusout(evt);
    this.popupToggleClickEvent = (evt) => this.popupToggleClick(evt);
    this.containerKeyupEvent = (evt) => this.containerKeyup(evt);
    this.popupOptionsClickEvent = (evt) => this.popupOptionsClick(evt);
    this._connectOptionsDispatchEvent = (evt) => this._connectOptionsDispatch(evt);

    this._connectOptions();
    this._connectToggle();
    this._onFocusOut();
    this.popupListMaxWidth();

    if (this.popoutInput && this.popoutInput.hasAttribute(selectors.popoutQuantity)) {
      document.addEventListener('theme:popout:update', this.updatePopout.bind(this));
    }
  }

  unload() {
    if (this.popoutOptions.length) {
      this.popoutOptions.forEach((element) => {
        element.removeEventListener('theme:popout:click', this.popupOptionsClickEvent);
        element.removeEventListener('click', this._connectOptionsDispatchEvent);
      });
    }

    this.popoutToggle.removeEventListener('click', this.popupToggleClickEvent);

    this.popoutToggle.removeEventListener('focusout', this.popupToggleFocusoutEvent);

    this.popoutList.removeEventListener('focusout', this.popupListFocusoutEvent);

    this.container.removeEventListener('keyup', this.containerKeyupEvent);
  }

  popupToggleClick(evt) {
    const ariaExpanded = evt.currentTarget.getAttribute(selectors.ariaExpanded) === 'true';

    if (this.productGrid) {
      const productGridItemImage = this.productGrid.querySelector(selectors.productGridImage);

      if (productGridItemImage) {
        productGridItemImage.classList.toggle(classes.visible, !ariaExpanded);
      }

      this.popoutList.style.maxHeight = `${Math.abs(this.popoutToggle.getBoundingClientRect().bottom - this.productGrid.getBoundingClientRect().bottom)}px`;
    }

    evt.currentTarget.setAttribute(selectors.ariaExpanded, !ariaExpanded);
    this.popoutList.classList.toggle(classes.listVisible);
    this.popupListMaxWidth();
  }

  popupToggleFocusout(evt) {
    const popoutLostFocus = this.container.contains(evt.relatedTarget);

    if (!popoutLostFocus) {
      this._hideList();
    }
  }

  popupListFocusout(evt) {
    const childInFocus = evt.currentTarget.contains(evt.relatedTarget);
    const isVisible = this.popoutList.classList.contains(classes.listVisible);

    if (isVisible && !childInFocus) {
      this._hideList();
    }
  }

  popupListMaxWidth() {
    this.popoutList.style.maxWidth = `${parseInt(document.body.clientWidth - this.popoutList.getBoundingClientRect().left)}px`;
  }

  popupOptionsClick(evt) {
    const link = evt.target.closest(selectors.popoutOptions);
    if (link.attributes.href.value === '#') {
      evt.preventDefault();

      let attrValue = '';

      if (evt.currentTarget.getAttribute(selectors.dataValue)) {
        attrValue = evt.currentTarget.getAttribute(selectors.dataValue);
      }

      this.popoutInput.value = attrValue;

      if (this.popoutPrevent) {
        this.popoutInput.dispatchEvent(new Event('change'));

        if (!evt.detail.preventTrigger && this.popoutInput.hasAttribute(selectors.popoutQuantity)) {
          this.popoutInput.dispatchEvent(new Event('input'));
        }

        const currentElement = this.popoutList.querySelector(`[class*="${classes.currentSuffix}"]`);
        let targetClass = classes.currentSuffix;

        if (currentElement && currentElement.classList.length) {
          for (const currentElementClass of currentElement.classList) {
            if (currentElementClass.includes(classes.currentSuffix)) {
              targetClass = currentElementClass;
              break;
            }
          }
        }

        const listTargetElement = this.popoutList.querySelector(`.${targetClass}`);

        if (listTargetElement) {
          listTargetElement.classList.remove(`${targetClass}`);
          evt.currentTarget.parentElement.classList.add(`${targetClass}`);
        }

        const targetAttribute = this.popoutList.querySelector(`[${selectors.ariaCurrent}]`);

        if (targetAttribute && targetAttribute.hasAttribute(`${selectors.ariaCurrent}`)) {
          targetAttribute.removeAttribute(`${selectors.ariaCurrent}`);
          evt.currentTarget.setAttribute(`${selectors.ariaCurrent}`, 'true');
        }

        if (attrValue !== '') {
          this.popoutToggle.textContent = attrValue;
        }

        this.popupToggleFocusout(evt);
        this.popupListFocusout(evt);
      } else {
        this._submitForm(attrValue);
      }
    }
  }

  updatePopout() {
    const targetElement = this.popoutList.querySelector(`[${selectors.dataValue}="${this.popoutInput.value}"]`);
    if (targetElement) {
      targetElement.dispatchEvent(
        new CustomEvent('theme:popout:click', {
          cancelable: true,
          bubbles: true,
          detail: {
            preventTrigger: true,
          },
        })
      );

      if (!targetElement.parentElement.nextSibling) {
        this.container.classList.add(classes.popoutAlternative);
      }
    } else {
      this.container.classList.add(classes.popoutAlternative);
    }
  }

  containerKeyup(evt) {
    if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
      return;
    }
    this._hideList();
    this.popoutToggle.focus();
  }

  bodyClick(evt) {
    const isOption = this.container.contains(evt.target);
    const isVisible = this.popoutList.classList.contains(classes.listVisible);

    if (isVisible && !isOption) {
      this._hideList();
    }
  }

  _connectToggle() {
    this.popoutToggle.addEventListener('click', this.popupToggleClickEvent);
  }

  _connectOptions() {
    if (this.popoutOptions.length) {
      this.popoutOptions.forEach((element) => {
        element.addEventListener('theme:popout:click', this.popupOptionsClickEvent);
        element.addEventListener('click', this._connectOptionsDispatchEvent);
      });
    }
  }

  _connectOptionsDispatch(evt) {
    const event = new CustomEvent('theme:popout:click', {
      cancelable: true,
      bubbles: true,
      detail: {
        preventTrigger: false,
      },
    });

    if (!evt.target.dispatchEvent(event)) {
      evt.preventDefault();
    }
  }

  _onFocusOut() {
    this.popoutToggle.addEventListener('focusout', this.popupToggleFocusoutEvent);

    this.popoutList.addEventListener('focusout', this.popupListFocusoutEvent);

    this.container.addEventListener('keyup', this.containerKeyupEvent);

    document.body.addEventListener('click', this.bodyClick.bind(this));
  }

  _submitForm() {
    const form = this.container.closest('form');
    if (form) {
      form.submit();
    }
  }

  _hideList() {
    this.popoutList.classList.remove(classes.listVisible);
    this.popoutToggle.setAttribute(selectors.ariaExpanded, false);
  }
}

const popoutSection = {
  onLoad() {
    sections[this.id] = [];
    const wrappers = this.container.querySelectorAll(selectors.popoutWrapper);
    wrappers.forEach((wrapper) => {
      sections[this.id].push(new Popout(wrapper));
    });
  },
  onUnload() {
    sections[this.id].forEach((popout) => {
      if (typeof popout.unload === 'function') {
        popout.unload();
      }
    });
  },
};

export {Popout, popoutSection};
